import { CartAddEvent } from '@theme/events';

class LadybirdPreorderController extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog') || document;
    this.productFormComponent = this.closest('product-form-component');
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.syncNotifyButton = this.syncNotifyButton.bind(this);

    this.ensurePdpSizingStyles();

    this.section.addEventListener('variant:update', this.handleVariantUpdate);
    this.section.addEventListener('click', this.handleClick);

    this.notifyObserver = new MutationObserver(this.syncNotifyButton);
    if (document.body) {
      this.notifyObserver.observe(document.body, { childList: true, subtree: true });
    }

    this.notifyRetryTimers = [250, 750, 1500, 3000].map((delay) =>
      window.setTimeout(this.syncNotifyButton, delay)
    );

    queueMicrotask(() => {
      this.positionPanels();
      this.syncNotifyButton();
    });
  }

  disconnectedCallback() {
    this.section?.removeEventListener('variant:update', this.handleVariantUpdate);
    this.section?.removeEventListener('click', this.handleClick);
    this.notifyObserver?.disconnect();
    this.notifyRetryTimers?.forEach((timer) => window.clearTimeout(timer));
    this.cleanupExternalPanels();
  }

  ensurePdpSizingStyles() {
    if (document.getElementById('ladybird-pdp-sizing-styles')) return;

    const style = document.createElement('style');
    style.id = 'ladybird-pdp-sizing-styles';
    style.textContent = `
      @media screen and (min-width: 750px) {
        .product-details .variant-option__button-label:not(.variant-option__button-label--has-swatch) {
          min-height: 44px !important;
          padding-block: 5px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  hasActiveAddToCart() {
    if (!this.productFormComponent) return false;

    const buttons = this.productFormComponent.querySelectorAll(
      'button[type="submit"], [ref="addToCartButton"], button[name="add"]'
    );

    return Array.from(buttons).some((button) => {
      if (!(button instanceof HTMLElement)) return false;
      const text = button.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() || '';
      const style = window.getComputedStyle(button);
      const visible = style.display !== 'none' && style.visibility !== 'hidden';
      const enabled = !button.hasAttribute('disabled') && button.getAttribute('aria-disabled') !== 'true';
      return visible && enabled && text.includes('add to cart');
    });
  }

  applyNotifySizing(element) {
    if (!(element instanceof HTMLElement)) return;

    element.dataset.ladybirdNotifySized = 'true';
    element.style.setProperty('width', '100%', 'important');
    element.style.setProperty('flex', '0 0 100%', 'important');
    element.style.setProperty('min-height', '44px', 'important');
    element.style.setProperty('height', '44px', 'important');
    element.style.setProperty('padding-top', '0', 'important');
    element.style.setProperty('padding-bottom', '0', 'important');
    element.style.setProperty('box-sizing', 'border-box', 'important');
    element.style.setProperty('font-size', '13px', 'important');
    element.style.setProperty('line-height', '1.2', 'important');
    element.style.setProperty('text-align', 'center', 'important');
  }

  syncNotifyElement(element) {
    if (!(element instanceof HTMLElement)) return;

    if (this.dataset.inStock === 'true') {
      element.dataset.ladybirdHiddenForStock = 'true';
      element.style.setProperty('display', 'none', 'important');
      return;
    }

    if (element.dataset.ladybirdHiddenForStock === 'true') {
      element.style.removeProperty('display');
      delete element.dataset.ladybirdHiddenForStock;
    }

    this.applyNotifySizing(element);
  }

  scanNotifyRoot(root, seenRoots = new Set()) {
    if (!root || seenRoots.has(root)) return;
    seenRoots.add(root);

    const candidates = root.querySelectorAll?.('button, a, [role="button"], input[type="button"], input[type="submit"]') || [];

    candidates.forEach((candidate) => {
      const rawText = candidate instanceof HTMLInputElement ? candidate.value : candidate.textContent;
      const text = rawText?.replace(/\s+/g, ' ').trim().toLowerCase() || '';

      if (text.includes('notify me')) {
        const buttonLike = candidate.closest?.('button, a, [role="button"]') || candidate;
        this.syncNotifyElement(buttonLike);
      }

      if (candidate.shadowRoot) {
        this.notifyObserver?.observe(candidate.shadowRoot, { childList: true, subtree: true });
        this.scanNotifyRoot(candidate.shadowRoot, seenRoots);
      }
    });

    const allElements = root.querySelectorAll?.('*') || [];
    allElements.forEach((element) => {
      if (element.shadowRoot) {
        this.notifyObserver?.observe(element.shadowRoot, { childList: true, subtree: true });
        this.scanNotifyRoot(element.shadowRoot, seenRoots);
      }
    });
  }

  syncNotifyButton() {
    this.scanNotifyRoot(this.productFormComponent || this.section);
  }

  cleanupExternalPanels() {
    if (!this.productFormComponent) return;

    this.productFormComponent.querySelectorAll('[data-ladybird-external]').forEach((node) => {
      if (node.dataset.ladybirdExternal === this.dataset.instanceId) node.remove();
    });

    this.productFormComponent.classList.remove('ladybird-has-preorder');
  }

  positionPanels() {
    if (!this.productFormComponent) return;

    this.cleanupExternalPanels();

    const panel = this.querySelector('.ladybird-preorder');
    const alternate = this.querySelector('[data-ladybird-alternate]');

    if (!panel && !alternate) {
      this.syncNotifyButton();
      return;
    }

    const productForm = this.closest('form') || this.productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    if (!productForm) return;

    if (alternate) {
      alternate.remove();
      alternate.dataset.ladybirdExternal = this.dataset.instanceId;

      if (panel) {
        productForm.insertAdjacentElement('afterend', alternate);
      } else {
        const productFormButtons =
          productForm.querySelector('[ref="productFormButtons"]') ||
          productForm.querySelector('.product-form-buttons');

        if (productFormButtons) {
          productFormButtons.insertAdjacentElement('beforeend', alternate);
        } else {
          productForm.insertAdjacentElement('afterend', alternate);
        }
      }
    }

    if (panel) {
      panel.dataset.ladybirdExternal = this.dataset.instanceId;
      productForm.insertAdjacentElement('beforebegin', panel);
      this.productFormComponent.classList.add('ladybird-has-preorder');
    }

    this.syncNotifyButton();
  }

  handleVariantUpdate(event) {
    const detail = event.detail || {};
    const eventProductId = detail.data?.productId;

    if (eventProductId && eventProductId !== this.dataset.productId) return;

    const html = detail.data?.html;
    if (!html) return;

    const selector = `ladybird-preorder-controller[data-product-id="${this.dataset.productId}"][data-instance-id="${this.dataset.instanceId}"]`;
    const next = html.querySelector(selector);

    this.cleanupExternalPanels();

    if (!next) {
      this.innerHTML = '';
      this.syncNotifyButton();
      return;
    }

    this.dataset.inStock = next.dataset.inStock;
    const currentShipping = this.section.querySelector('[data-lbs-dress-shipping]');
    const nextShipping = html.querySelector('[data-lbs-dress-shipping]');
    if (currentShipping && nextShipping) currentShipping.innerHTML = nextShipping.innerHTML;
    this.innerHTML = next.innerHTML;
    queueMicrotask(() => {
      this.positionPanels();
      this.syncNotifyButton();
    });
  }

  async handleClick(event) {
    const button = event.target.closest('[data-ladybird-preorder-button]');
    if (!button || button.dataset.ladybirdInstance !== this.dataset.instanceId) return;

    if (button.dataset.previewOnly === 'true') {
      const status = button.closest('.ladybird-preorder')?.querySelector('[data-lbs-preorder-status]');
      if (status) status.textContent = button.dataset.previewMessage;
      return;
    }

    const variantId = Number(button.dataset.preorderVariantId);
    if (!variantId) return;

    const quantityInput = this.productFormComponent?.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);
    const label = button.querySelector('[data-ladybird-preorder-label]');
    const originalLabel = label?.textContent || 'PRE-ORDER — SHIPS OCTOBER 15';
    const selection = button.dataset.selection || '';

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    button.disabled = true;
    if (label) label.textContent = 'ADDING PRE-ORDER…';

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
              properties: {
                'Pre-order': button.dataset.shippingEstimate || 'Ships October 15',
                'Reserved selection': selection,
              },
            },
          ],
          sections: sectionIds.join(','),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add preorder');
      }

      let cart;
      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (_) {
        cart = undefined;
      }

      this.dispatchEvent(
        new CartAddEvent(cart, variantId.toString(), {
          source: 'product-form-component',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );

      if (label) label.textContent = 'ADDED TO CART';

      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
        button.disabled = false;
      }, 1200);
    } catch (error) {
      console.error('Lady Bird preorder error:', error);
      if (label) label.textContent = 'PLEASE TRY AGAIN';
      button.disabled = false;

      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
      }, 2200);
    }
  }
}

if (!customElements.get('ladybird-preorder-controller')) {
  customElements.define('ladybird-preorder-controller', LadybirdPreorderController);
}

